import { createApp } from 'vue';
import { createRouter, createWebHistory } from 'vue-router';
import App from './App.vue';
import './assets/tailwind.css';

// Import Supabase and logger
import { supabase } from './services/supabase';
import { logger, LogCategory } from './services/logger';
import {initializeKendoLicense} from "@/utils/kendo-license";

// Extend Window interface for TypeScript
declare global {
  interface Window {
    supabase: any;
    kendo: any;
    kendoLicenseInitializer?: () => boolean;
  }
}

initializeKendoLicense();

// Create the Vue app
const app = createApp(App);

// Create router
const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/',
      name: 'home',
      component: App,
      meta: {
        navItem: 'dashboard'
      }
    },
    {
      path: '/dashboard',
      name: 'dashboard',
      component: App,
      meta: {
        navItem: 'dashboard'
      }
    },
    {
      path: '/email-generator',
      name: 'email-generator',
      component: App,
      meta: {
        navItem: 'email-generator'
      }
    },
    {
      path: '/forecasting-planners',
      name: 'forecasting-planners',
      component: App,
      meta: {
        navItem: 'forecasting-planners'
      }
    },
    {
      path: '/forecasting-pco',
      name: 'forecasting-pco',
      component: App,
      meta: {
        navItem: 'forecasting-pco'
      }
    },
    {
      path: '/administration',
      name: 'administration',
      component: App,
      meta: {
        navItem: 'administration',
        adminSubView: ''
      }
    },
    {
      path: '/administration/email-templates',
      name: 'email-templates',
      component: App,
      meta: {
        navItem: 'administration',
        adminSubView: 'email-templates'
      }
    },
    {
      path: '/administration/user-roles',
      name: 'user-roles',
      component: App,
      meta: {
        navItem: 'administration',
        adminSubView: 'user-roles'
      }
    },
    {
      path: '/administration/system-config',
      name: 'system-config',
      component: App,
      meta: {
        navItem: 'administration',
        adminSubView: 'system-config'
      }
    },
    {
      path: '/administration/audit-logs',
      name: 'audit-logs',
      component: App,
      meta: {
        navItem: 'administration',
        adminSubView: 'audit-logs'
      }
    },
    {
      path: '/supabase-test',
      name: 'supabase-test',
      component: App,
      meta: {
        navItem: 'supabase-test'
      }
    },
    {
      path: '/storage-test',
      name: 'storage-test',
      component: App,
      meta: {
        navItem: 'storage-test'
      }
    },
    {
      path: '/user-settings',
      name: 'user-settings',
      component: App,
      meta: {
        navItem: 'user-settings'
      }
    },
    {
      path: '/template-storage',
      name: 'template-storage',
      component: App,
      meta: {
        navItem: 'template-storage'
      }
    },
    // Catch-all route for 404
    {
      path: '/:pathMatch(.*)*',
      redirect: '/'
    }
  ],
});

// Add router to the app
app.use(router);

// Initialize auth state listener
supabase.auth.onAuthStateChange((event, session) => {
  logger.info('Auth state changed', LogCategory.AUTH, { event, userId: session?.user?.id });
});

// Check for existing session on app load
const initializeAuth = async () => {
  try {
    const { data } = await supabase.auth.getSession();
    logger.info('Session data on app load', LogCategory.AUTH, {
      hasSession: !!data.session,
      userId: data.session?.user?.id
    });

    // Here you might want to set the user in a store or reactive variable
    // For example, if using Pinia or Vuex
  } catch (error) {
    logger.error('Failed to get auth session', LogCategory.AUTH, { error });
  }
};

// Initialize auth before mounting
initializeAuth().then(() => {
  // Make Supabase available globally for debugging in development only
  if (process.env.NODE_ENV !== 'production') {
    window.supabase = supabase;
  }

  // Mount the app
  app.mount('#app');

  // Log Supabase initialization
  logger.info('Supabase client initialized', LogCategory.GENERAL);
});
